/* eslint-disable @typescript-eslint/no-use-before-define */
import {
  ListMask,
  useScrollableMask,
} from '@euler/app/components/list/ListMask';
import { Typography } from '@euler/app/components/typography';
import { useTaskHeaderMetrics } from '@euler/app/flows/task/functions/useTaskHeaderMetrics';
import { Img } from '@euler/components/image/AdvancedImage';
import { kAppStoreSnapshotMode } from '@euler/functions/constants';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import * as MatrixMath from '@euler/utils/MatrixMath';
import { LinearGradient } from 'expo-linear-gradient';
import React, { memo } from 'react';
import { StyleSheet, View } from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';
import Animated, {
  Extrapolate,
  SharedValue,
  interpolate,
  useAnimatedStyle,
  useSharedValue,
} from 'react-native-reanimated';

export const TaskDetailPageHeader = memo(
  ({
    vehicleImageUrl,
    vehicleName,
    licensePlateNo,
    scrollY,
    offsetTop,
    headerHeight,
  }: {
    vehicleImageUrl?: string;
    vehicleName: string;
    licensePlateNo: string;
    scrollY: SharedValue<number>;
    offsetTop: number;
    headerHeight: number;
  }) => {
    const { rpx } = useResponsiveContext();

    const {
      safeAreaFrame,
      navBarHeight,
      headerMaxExtend,
      offset,
      initialImageScale,
      ...metrics
    } = useTaskHeaderMetrics();

    const maxScale =
      ((safeAreaFrame.width / 2 - offset) * initialImageScale) /
      metrics.vehicleInfoHeight;

    const vehicleInfoWidth = useSharedValue(0);
    const vehicleInfoHeight = useSharedValue(0);

    const animatedStyle = useAnimatedStyle(() => ({
      height:
        interpolate(
          scrollY.value,
          [-headerMaxExtend, 0, headerHeight - navBarHeight - offsetTop],
          [headerHeight + headerMaxExtend, headerHeight, navBarHeight],
          Extrapolate.CLAMP,
        ) - offsetTop,
    }));

    const imageStyle = useAnimatedStyle(() => ({
      transform: [
        {
          scale: interpolate(
            scrollY.value,
            [-headerMaxExtend, 0, navBarHeight],
            [maxScale, initialImageScale, 0],
            Extrapolate.CLAMP,
          ),
        },
      ],
    }));

    const vehicleInfoStyle = useAnimatedStyle(() => {
      const scale = interpolate(
        scrollY.value,
        [0, navBarHeight],
        [1, 0],
        Extrapolate.CLAMP,
      );
      const matrix = transformScale(
        scale,
        vehicleInfoWidth.value,
        vehicleInfoHeight.value,
      );
      return {
        opacity: interpolate(
          scrollY.value,
          [-headerMaxExtend, 0, navBarHeight],
          [1, 1, 0],
          Extrapolate.CLAMP,
        ),
        transform: [
          {
            matrix,
          },
        ],
      };
    });

    return (
      <Animated.View
        css={`
          position: absolute;
          left: 0;
          top: 0;
          right: 0;
          padding-top: ${navBarHeight}px;
        `}
        style={animatedStyle}
      >
        <Animated.View
          css={`
            position: absolute;
            left: 0;
            bottom: 0;
            right: ${rpx(120)}px;
          `}
          style={vehicleInfoStyle}
          onLayout={e => {
            vehicleInfoWidth.value = e.nativeEvent.layout.width;
            vehicleInfoHeight.value = e.nativeEvent.layout.height;
          }}
        >
          <VehicleInfoView
            licensePlateNo={kAppStoreSnapshotMode ? '沪AAAAAA' : licensePlateNo}
            vehicleName={vehicleName}
          />
        </Animated.View>

        <Animated.View
          pointerEvents={'none'}
          css={`
            position: absolute;
            bottom: ${(metrics.vehicleInfoHeight - metrics.vehicleImageHeight) /
            2}px;
            right: ${rpx(-15)}px;
            width: ${metrics.vehicleImageWidth}px;
            height: ${metrics.vehicleImageHeight}px;
          `}
          style={imageStyle}
        >
          <Img
            style={StyleSheet.absoluteFill}
            source={
              vehicleImageUrl
                ? {
                    uri: vehicleImageUrl,
                  }
                : require('@euler/assets/img/default-car-img.png')
            }
          />
        </Animated.View>
      </Animated.View>
    );
  },
);

const VehicleInfoView = memo(
  ({
    vehicleName,
    licensePlateNo,
  }: {
    vehicleName: string;
    licensePlateNo: string;
  }) => {
    const { rpx } = useResponsiveContext();
    const { vehicleInfoHeight } = useTaskHeaderMetrics();
    const {
      showLeadingMask,
      showTrailingMask,
      onLayout,
      onContentSizeChange,
      onScroll,
    } = useScrollableMask({
      orientation: 'horizontal',
    });

    return (
      <View
        css={`
          padding: ${rpx(15)}px 0 ${rpx(15)}px ${rpx(20)}px;
          height: ${vehicleInfoHeight}px;
        `}
      >
        <ListMask leadingMask={showLeadingMask} trailingMask={showTrailingMask}>
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            scrollEventThrottle={1}
            onScroll={onScroll}
            onLayout={onLayout}
            onContentSizeChange={onContentSizeChange}
            contentContainerStyle={{
              flexGrow: 1,
            }}
          >
            <Typography.Label
              light
              color="white"
              size={rpx(24)}
              numberOfLines={1}
              lineHeight={rpx(32)}
            >
              {vehicleName}
            </Typography.Label>
          </ScrollView>
        </ListMask>
        <Typography.Label
          thin
          color="white"
          size={rpx(16)}
          lineHeight={rpx(28)}
        >
          {licensePlateNo}
        </Typography.Label>
      </View>
    );
  },
);

export const TaskDetailHeaderShade = memo(
  ({
    scrollY,
    offsetTop,
    headerHeight,
    colors = ['#006be4', '#5ba0e0'],
  }: {
    scrollY: SharedValue<number>;
    offsetTop: number;
    headerHeight: number;
    colors?: string[];
  }) => {
    const { navBarHeight, headerMaxExtend } = useTaskHeaderMetrics();
    const animatedStyle = useAnimatedStyle(() => ({
      height: interpolate(
        scrollY.value,
        [-headerMaxExtend, 0, headerHeight - navBarHeight - offsetTop],
        [headerHeight + headerMaxExtend, headerHeight, navBarHeight],
        Extrapolate.CLAMP,
      ),
    }));
    return (
      <Animated.View style={[StyleSheet.absoluteFill, animatedStyle]}>
        <LinearGradient
          start={{ x: 0, y: 0.5 }}
          end={{ x: 1, y: 1 }}
          colors={colors}
          locations={[0.335, 1]}
          css={`
            flex: 1;
          `}
        />
      </Animated.View>
    );
  },
);

function applyTransformOrigin(
  matrix: MatrixMath.Matrix3D,
  origin: MatrixMath.Point3D,
) {
  'worklet';
  const x = origin.x;
  const y = origin.y;
  const z = origin.z;
  const translate = MatrixMath.createIdentityMatrix();
  MatrixMath.reuseTranslate3dCommand(translate, x, y, z);
  MatrixMath.multiplyInto(matrix, translate, matrix);
}

function getScaleMatrix(x: number): MatrixMath.Matrix3D {
  'worklet';
  return [x, 0, 0, 0, 0, x, 0, 0, 0, 0, x, 0, 0, 0, 0, 1];
}

function transformScale(scaleBy: number, width: number, height: number) {
  'worklet';
  const matrix = MatrixMath.createIdentityMatrix();
  const toScale = getScaleMatrix(scaleBy);

  applyTransformOrigin(matrix, {
    x: (width * scaleBy - width) / 2,
    y: (height * scaleBy - height) / 2,
    z: 0,
  });

  MatrixMath.multiplyInto(matrix, matrix, toScale);

  return matrix;
}
